import { useEffect, useMemo, useState, type ReactNode } from "react";
import { TimeLens } from "./TimeLens";
import { useHash } from "./ui";
import { useStore } from "../store";
import type { Bible } from "../domain/types";

const ITEMS: Array<{ href: string; label: string; short: string }> = [
  { href: "/", label: "Tableau de bord", short: "Board" },
  { href: "/chronologie", label: "Chronologie", short: "Temps" },
  { href: "/personnages", label: "Personnages", short: "Persos" },
  { href: "/relations", label: "Relations", short: "Liens" },
  { href: "/monde", label: "Monde", short: "Monde" },
  { href: "/arcs", label: "Arcs narratifs", short: "Arcs" },
  { href: "/secrets", label: "Secrets", short: "Secrets" },
  { href: "/continuite", label: "Continuité", short: "Alertes" },
  { href: "/contexte", label: "Contexte d’écriture", short: "Écrire" },
  { href: "/sources", label: "Sources", short: "Sources" },
  { href: "/parametres", label: "Paramètres", short: "Plus" },
];

export function Layout({
  bible,
  children,
}: {
  bible: Bible;
  children: ReactNode;
}) {
  const [hash, navigate] = useHash();
  const { dirty, online, syncState, error, save, undo, revisionNumber, updatedAt } = useStore();
  const [more, setMore] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const path = hash.split("?")[0];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return [];
    const out: Array<{ href: string; title: string; kind: string }> = [];
    for (const c of bible.characters) {
      if (`${c.name} ${c.aliases?.join(" ") ?? ""} ${c.className}`.toLowerCase().includes(needle)) {
        out.push({ href: `/personnages/${c.id}`, title: c.name, kind: "Personnage" });
      }
    }
    for (const e of bible.events) {
      if (`${e.title} ${e.summary}`.toLowerCase().includes(needle)) {
        out.push({ href: `/chronologie?e=${e.id}`, title: e.title, kind: "Événement" });
      }
    }
    for (const s of bible.secrets) {
      if (`${s.title} ${s.objectiveTruth}`.toLowerCase().includes(needle)) {
        out.push({ href: `/secrets`, title: s.title, kind: "Secret" });
      }
    }
    return out.slice(0, 20);
  }, [bible, q]);

  const go = (href: string) => {
    navigate(href);
    setMore(false);
    setSearchOpen(false);
  };

  const syncClass = syncState === "offline" || !online ? "off" : syncState === "error" || syncState === "conflict" ? "bad" : "";

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <small>Archive du Flux</small>
          <strong className="display">Protocole d’Ascension</strong>
        </div>
        {ITEMS.map((it) => (
          <button
            key={it.href}
            className={`nav-btn ${path === it.href || (it.href !== "/" && path.startsWith(it.href)) ? "active" : ""}`}
            type="button"
            onClick={() => go(it.href)}
          >
            {it.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button className="nav-btn" type="button" onClick={() => setSearchOpen(true)}>
          Recherche <span className="kbd">Ctrl K</span>
        </button>
      </aside>
      <div className="main">
        <header className="topbar">
          <TimeLens bible={bible} />
          <div className="row space" style={{ marginTop: "0.55rem" }}>
            <div className="row tiny">
              <span className={`sync-dot ${syncClass}`} />
              {online ? "En ligne" : "Hors ligne"}
              {" · "}rév. {revisionNumber}
              {updatedAt ? ` · ${updatedAt}` : ""}
              {dirty ? " · modifications non sauvegardées" : ""}
              {syncState === "saving" ? " · enregistrement…" : ""}
            </div>
            <div className="row">
              <button className="btn" type="button" onClick={undo} disabled={!dirty}>
                Annuler
              </button>
              <button className="btn primary" type="button" onClick={() => void save()}>
                Enregistrer
              </button>
              <button className="btn violet" type="button" onClick={() => go("/contexte")}>
                Préparer l’écriture
              </button>
            </div>
          </div>
          {error && <p className="warn tiny">{error}</p>}
        </header>
        <main className="page">{children}</main>
      </div>
      <nav className="bottom-nav" aria-label="Navigation mobile">
        {ITEMS.slice(0, 4).map((it) => (
          <button key={it.href} className={path === it.href || (it.href !== "/" && path.startsWith(it.href)) ? "active nav-btn" : "nav-btn"} type="button" onClick={() => go(it.href)}>
            {it.short}
          </button>
        ))}
        <button className="nav-btn" type="button" onClick={() => setMore((v) => !v)}>
          Plus
        </button>
      </nav>
      {more && (
        <div className="modal-back" onClick={() => setMore(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Plus</h2>
            {ITEMS.slice(4).map((it) => (
              <button key={it.href} className="item" type="button" onClick={() => go(it.href)}>
                {it.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {searchOpen && (
        <div className="search-overlay" onClick={() => setSearchOpen(false)}>
          <div className="search-box card" onClick={(e) => e.stopPropagation()}>
            <input
              className="search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Personnage, événement, secret…"
            />
            <div className="list" style={{ marginTop: 12 }}>
              {results.map((r) => (
                <button key={r.href + r.title} className="item" type="button" onClick={() => go(r.href)}>
                  <span className="tiny">{r.kind}</span>
                  <div>{r.title}</div>
                </button>
              ))}
              {q.length >= 2 && results.length === 0 && <p className="empty">Aucun résultat.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
